import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import { LyneDatePicker } from "components/Lyne/LyneDatePicker";
import { formatDate, isDateNewerThanYesterday } from "helpers";
import { createLogger } from "logging";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

const logger = createLogger("HookForm:DatePicker");

interface HookFormDatePickerProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
}

export const HookFormDatePicker = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormDatePickerProps<TFieldValues, TName>
) => {
  const { name, control, required, label, contextHelp } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        validate: (value) => {
          if (!value) return "";
          if (!isDateNewerThanYesterday(value)) return "Date must be newer than yesterday";
          return "";
        },
      }}
      render={({ field, fieldState }) => {
        const { onChange, value, disabled } = field;

        const { isDirty, isTouched, error } = fieldState;
        const className = classNames(
          isDirty ? "is-dirty" : "",
          isTouched ? "is-touched" : "",
          error ? "is-error" : "",
          required && isTouched && !value ? "sbb-invalid" : "",
          !isDateNewerThanYesterday(value) ? "sbb-invalid" : ""
        );
        logger.debug(`${name}: state -> `, className);

        // handle onChange event, convert Date value to hook form
        // internal format is dd.MM.yyyy
        const onChangeDate = (value: Date | undefined) => {
          if (value) {
            const valueToUse = formatDate(value);
            if (valueToUse) {
              logger.debug(`onChangeDate(): `, value, "->", valueToUse);
              onChange(valueToUse);
              return;
            }
          }
          logger.debug(`onChangeDate(): empty -> undefined`);
          onChange(undefined);
        };

        return (
          <LyneDatePicker
            name={name}
            label={label}
            className={className}
            value={value}
            disabled={disabled}
            error={error?.message}
            onChange={onChangeDate}
            contextHelp={contextHelp}
          />
        );
      }}
    />
  );
};
